import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { actualizarPerfil, getPadron } from '../../../../pages/Contracts/Vencimientos/contracts.services'
import DialogPadron from './DialogDataAfip'
import { IPadron, IPerfil } from '../../../../dto/ContractDTO'
import SnackBarUtils from '../../../../utils/SnackBarUtils'
import ConfirmDialog from '../../../../pages/Products/components/ConfirmDialog'

interface DocType {
  key: string
  label: string
}

const DocTypes: DocType[] = [
  {
    label: 'CUIT',
    key: 'cuit'
  },
  {
    label: 'DNI',
    key: 'dni'
  }
]

const PerfilFact: React.FC<{
  codLocal: string
  perfil: IPerfil
  consultarPerfil: () => Promise<void>
}> = ({ codLocal, perfil, consultarPerfil }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [docType, setDocType] = useState<'cuit' | 'dni'>('cuit')
  const [newPerfil, setNewPerfil] = useState<IPerfil>(null)
  const [dataPadron, setDataPadron] = useState<IPadron>(null)
  const [openDataPadron, setOpenDataPadron] = useState<boolean>(false)
  const [noChanges, setNoChanges] = useState<boolean>(false)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false
    }))
  }

  const consultarPadron = useCallback(async () => {
    if (!newPerfil.numeroIdentificacionFiscal) return SnackBarUtils.error('Debes agregar un número de CUIT')
    try {
      setLoading(true)
      const res = await getPadron(newPerfil.numeroIdentificacionFiscal)
      setDataPadron(res.data)
      setOpenDataPadron(true)
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, [newPerfil])

  const handleActualizarPerfil = useCallback(
    async (p: IPerfil) => {
      if (!p.noFacturarASuNombre && !p.numeroIdentificacionFiscal)
        return SnackBarUtils.error('Debes agregar un número de DNI')
      try {
        setLoading(false)
        await actualizarPerfil(codLocal, p)
        SnackBarUtils.success('Se actualizó la información de facturación.')
        consultarPerfil()
      } catch (e) {
        console.log(e)
        SnackBarUtils.error(
          e.message === 'Network Error' ? 'Error al actualizar la información de facturación' : e.message
        )
      } finally {
        setLoading(false)
      }
    },
    [codLocal, newPerfil]
  )

  const handleConsumidorFinal = useCallback(
    (checked: boolean) => {
      if (checked && perfil.noFacturarASuNombre) return
      setConfirmDialog({
        open: true,
        text: {
          title: 'Se facturará a consumidor final sin nombre',
          body: '¿Deseas continuar?',
          leftButton: 'Cancelar',
          rightButton: 'Confirmar',
          colorButtonConfirm: 'error'
        },
        action: () => {
          handleActualizarPerfil({ ...newPerfil, noFacturarASuNombre: checked })
          clearConfirmDialog()
        },
        close: () => clearConfirmDialog()
      })
    },
    [codLocal, newPerfil]
  )

  const handleDocTypeChange = useCallback(
    (value: 'cuit' | 'dni') => {
      setDocType(value)
      setNewPerfil({
        ...newPerfil,
        tipoIdentificacionFiscal: DocTypes.find((d) => d.key === value).label
      })
    },
    [newPerfil]
  )

  useEffect(() => {
    if (!perfil || !newPerfil) return
    const isEqual =
      newPerfil.tipoIdentificacionFiscal === perfil.tipoIdentificacionFiscal &&
      newPerfil.numeroIdentificacionFiscal === perfil.numeroIdentificacionFiscal &&
      newPerfil.noFacturarASuNombre === perfil.noFacturarASuNombre &&
      newPerfil.nombreFiscal === perfil.nombreFiscal

    setNoChanges(isEqual)
  }, [newPerfil])

  useEffect(() => {
    if (perfil) {
      setNewPerfil(perfil)
    }
  }, [perfil])

  if (!codLocal || !newPerfil) return

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', ...(isMobile && { marginLeft: '1.5rem' }) }}>
        <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Selector de tipo */}
          <FormControl size="small">
            <Select
              value={docType || 'cuit'}
              onChange={(e) => handleDocTypeChange(e.target.value as 'cuit' | 'dni')}
              disabled={loading || newPerfil.noFacturarASuNombre}
              sx={{ height: '40px', minWidth: '90px' }}
            >
              {DocTypes.map((doc) => (
                <MenuItem key={doc.key} value={doc.key}>
                  {doc.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Número de documento */}
          <TextField
            value={newPerfil.numeroIdentificacionFiscal || ''}
            onChange={(e) =>
              setNewPerfil({
                ...newPerfil,
                numeroIdentificacionFiscal: e.target.value || null
              })
            }
            variant="outlined"
            disabled={loading || newPerfil.noFacturarASuNombre}
            size="small"
            placeholder={docType === 'cuit' ? 'XX-XXXXXXXX-X' : 'XXXXXXXX'}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />

          {/* Nombre y apellido — solo visible para DNI */}
          {docType === 'dni' && (
            <TextField
              value={newPerfil?.nombreFiscal || ''}
              onChange={(e) => setNewPerfil({ ...newPerfil, nombreFiscal: e.target.value })}
              variant="outlined"
              disabled={loading || newPerfil.noFacturarASuNombre}
              size="small"
              placeholder="Nombre y apellido"
              sx={{ '& .MuiInputBase-root': { height: '40px' }, maxWidth: '280px' }}
            />
          )}

          <Button
            disabled={loading || newPerfil.noFacturarASuNombre || noChanges}
            variant="contained"
            color="success"
            size={isMobile ? 'small' : 'medium'}
            onClick={() => (docType === 'cuit' ? consultarPadron() : handleActualizarPerfil(newPerfil))}
            sx={{ height: '40px', ...(isMobile && { whiteSpace: 'nowrap', px: 2 }) }}
          >
            Verificar
          </Button>
        </Box>

        <FormControlLabel
          sx={{ flex: 1 }}
          control={
            <Checkbox
              checked={newPerfil.noFacturarASuNombre}
              onChange={(e) => {
                const checked = e.target.checked
                setNewPerfil({ ...newPerfil, noFacturarASuNombre: checked })
                if (checked) handleConsumidorFinal(checked)
              }}
            />
          }
          label="No facturar a su nombre"
        />
      </Box>

      <DialogPadron
        open={openDataPadron}
        setOpen={setOpenDataPadron}
        codLocal={codLocal}
        dataPadron={dataPadron}
        isMobile={isMobile}
        updateData={consultarPerfil}
      />
      <ConfirmDialog {...confirmDialog} />
    </>
  )
}

export default PerfilFact
